/**
 * Formats a phone number like: +251 (947)-710-673
 * Handles various stored formats: +251947710673, 0947710673, 947710673, etc.
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "—";

  // Remove all non-digit and non-plus characters
  let cleaned = phone.replace(/[^\d+]/g, "");
  if (!cleaned) return phone;

  // Extract country code and local number
  let countryCode = "";
  let local = "";

  if (cleaned.startsWith("+")) {
    // Try to match known country code lengths (1-3 digits after +)
    // Match longest first
    const withoutPlus = cleaned.slice(1);
    // Common 3-digit codes
    const three = withoutPlus.slice(0, 3);
    const two = withoutPlus.slice(0, 2);
    const one = withoutPlus.slice(0, 1);

    // 3-digit country codes (e.g., +251, +234, +971, etc.)
    if (withoutPlus.length > 3) {
      countryCode = "+" + three;
      local = withoutPlus.slice(3);
    } else {
      return phone; // Too short to format
    }

    // Adjust for 1-digit codes (+1, +7) and 2-digit codes (+44, +91, etc.)
    const oneDigitCodes = ["1", "7"];
    const twoDigitCodes = [
      "20", "27", "30", "31", "32", "33", "34", "36", "39", "40", "41",
      "43", "44", "45", "46", "47", "48", "49", "51", "52", "54", "55",
      "56", "57", "58", "60", "61", "62", "63", "64", "65", "66", "81",
      "82", "84", "86", "90", "91", "92", "94", "98",
    ];

    if (oneDigitCodes.includes(one)) {
      countryCode = "+" + one;
      local = withoutPlus.slice(1);
    } else if (twoDigitCodes.includes(two)) {
      countryCode = "+" + two;
      local = withoutPlus.slice(2);
    }
  } else {
    // No country code, just format the digits
    local = cleaned.replace(/^0+/, "");
    countryCode = "";
  }

  // Remove leading zero from local
  local = local.replace(/^0+/, "");

  if (local.length < 4) return phone;

  // Format local number in groups: (XXX)-XXX-XXXX or similar
  let formatted = "";
  if (local.length >= 9) {
    // (XXX)-XXX-XXX or (XXX)-XXX-XXXX
    const areaCode = local.slice(0, 3);
    const mid = local.slice(3, 6);
    const last = local.slice(6);
    formatted = `(${areaCode})-${mid}-${last}`;
  } else if (local.length >= 7) {
    const areaCode = local.slice(0, 3);
    const rest = local.slice(3);
    formatted = `(${areaCode})-${rest}`;
  } else {
    // Short numbers, just group
    formatted = local;
  }

  return countryCode ? `${countryCode} ${formatted}` : formatted;
};
